import { DiscoverPortalActivityAction } from 'models/DiscoverPortalActivityAction';
import Workflow, { ActionStatus, WorkflowAction, WorkflowStatus } from 'models/Workflow';

import {
  isDeniedOutcome,
  OUTCOME_DENIED,
  OUTCOME_NOT_SELECTED,
  OUTCOME_SELECTED,
  PORTAL_DISCOVERY_API_NAME,
  recordedOutcome,
} from './constants';
import { Tone } from './StatusBadge';

// The list's status vocabulary (the mockup's pills / legend). Distinct from the per-action
// badge labels — the list speaks in "Action Needed / Processing / Ready for Review / Complete".
export type ListStatus =
  | 'archived'
  | 'actionNeeded'
  | 'readyForReview'
  | 'processing'
  | 'complete'
  | 'error'
  | 'pending'
  | 'canceled'
  // The recorded Referral Outcome (OLIO-3172). Only ever produced by the whole-run rollup, never
  // by a category — no category's actions add up to "Not Selected", and the progress bar's
  // segments must keep speaking in lifecycle states.
  | 'selected'
  | 'notSelected'
  // Denied (OLIO-3170) is both a recorded outcome — every facility denied, the API closed the
  // referral — and, on the Active tab, a viewer-relative reading: every facility THIS viewer holds
  // has denied while other buildings are still deciding. One status, one pill, for both.
  | 'denied';

// The quiet-pending Referral Outcome (OLIO-2611): the engine keeps it Pending so it never
// poisons cross-referral attention (list pills, notifications), but ON the referral page —
// the card badge and the rail, per the design — it presents as ready, because it is always
// recordable. Deliberately NOT folded into categoryStatus: the list rollups must stay quiet.
export function isQuietlyReady(action: WorkflowAction): boolean {
  return action.kind === 'AcceptReferral' && action.isPending;
}

// Every row has come to rest and at least one actually completed — skipped (not
// applicable) and canceled rows resting beside completed work read as Complete, not
// Pending. Shared by the card badge rollup and the rail's indicator (one predicate,
// two surfaces — they must never disagree).
// The shape these rollups actually read. WorkflowAction satisfies it, and so does the admin run
// console's plain step type once it supplies `isCompleted` — which is the point: one precedence
// rule rather than two copies that drift apart, which is exactly what the console and this page
// must never do about a status.
export type RollupAction = {
  status: string;
  effectiveStatus: string;
  isCompleted: boolean;
};

export function completeWithParked(actions: RollupAction[]): boolean {
  return (
    actions.length > 0 &&
    actions.some((a) => a.isCompleted) &&
    actions.every((a) => a.isCompleted || a.status === ActionStatus.CANCELED || a.status === ActionStatus.SKIPPED)
  );
}

// A category's rolled-up status — the most significant state across its actions (same priority
// the category card header uses): an errored action wins (a failure must surface even when
// something else is ready for review), then blocked/flagged, then ready-for-review, then done,
// then in-flight.
export function categoryStatus(actions: RollupAction[]): ActionStatus {
  // Presentation priority reads effectiveStatus (a completed DenialCheck match / no-active-coverage
  // eligibility present as flagged); terminality (completeWithParked, canceled/skipped) reads the
  // real engine status — "has it come to rest" is a lifecycle fact, not a presentation one. Mirrors
  // rollupIndicator (StatusIcon.tsx) so the card badge and the rail icon never disagree. ERRORED
  // leads so a failed step is never masked by a flagged / ready-for-review one.
  //
  // The at-rest branch below distinguishes canceled from skipped where rollupIndicator does not,
  // and they still agree: both resolve to a neutral badge and a muted icon, so only the WORD
  // differs. That is the whole point — the icon has nothing to say here, the label does.
  if (actions.some((a) => a.effectiveStatus === ActionStatus.ERRORED)) return ActionStatus.ERRORED;
  if (actions.some((a) => a.effectiveStatus === ActionStatus.FLAGGED)) return ActionStatus.FLAGGED;
  if (actions.some((a) => a.effectiveStatus === ActionStatus.ACTION_REQUIRED)) return ActionStatus.ACTION_REQUIRED;
  if (completeWithParked(actions)) return ActionStatus.COMPLETED;
  if (actions.some((a) => a.effectiveStatus === ActionStatus.RUNNING)) return ActionStatus.RUNNING;
  return atRestStatus(actions) ?? ActionStatus.PENDING;
}

// A category where nothing is left to do. Canceled and skipped are both at rest but they do NOT
// mean the same thing to a coordinator: canceled says the work was stopped, skipped says it never
// applied, which is why the badge reads them as "Canceled" and "Not Applicable" respectively.
//
// They used to roll up as one bucket labelled Canceled. That was invisible while an all-at-rest
// category almost always meant cancellation, but guards (OLIO-2613) make an all-skipped category
// ordinary — a referral with no documents parks its clinical assessment for every building — and it
// then read as though something had been stopped.
//
// A mixed category still reads Canceled: something really was stopped, and that is the more
// consequential half to surface.
function atRestStatus(actions: RollupAction[]): ActionStatus | null {
  if (actions.length === 0) return null;
  if (actions.every((a) => a.status === ActionStatus.SKIPPED)) return ActionStatus.SKIPPED;
  if (actions.every((a) => a.status === ActionStatus.CANCELED || a.status === ActionStatus.SKIPPED))
    return ActionStatus.CANCELED;

  return null;
}

const STATUS_TO_LIST: Record<ActionStatus, ListStatus> = {
  [ActionStatus.FLAGGED]: 'actionNeeded',
  [ActionStatus.ACTION_REQUIRED]: 'readyForReview',
  [ActionStatus.ERRORED]: 'error',
  [ActionStatus.RUNNING]: 'processing',
  [ActionStatus.COMPLETED]: 'complete',
  [ActionStatus.PENDING]: 'pending',
  [ActionStatus.CANCELED]: 'pending',
  [ActionStatus.SKIPPED]: 'pending',
};

// `label` is the per-row status pill; `legendLabel` (when present) is the legend swatch caption.
// They diverge for `processing`: the pill reads "Processing" (with the AI sparkle) while the
// legend names the state "Processing".
export const LIST_STATUS_DISPLAY: Record<ListStatus, { label: string; legendLabel?: string; tone: Tone }> = {
  actionNeeded: { label: 'Flagged', tone: 'warning' },
  readyForReview: { label: 'Ready for Review', tone: 'review' },
  processing: { label: 'Processing', legendLabel: 'Processing', tone: 'running' },
  complete: { label: 'Complete', tone: 'success' },
  error: { label: 'Error', tone: 'danger' },
  pending: { label: 'Pending', tone: 'neutral' },
  canceled: { label: 'Canceled', tone: 'neutral' },
  // Same terminal status as `canceled`, different word, because a person chose it (OLIO-2599).
  // Not in LEGEND: the legend explains in-flight work, and neither of these is that.
  archived: { label: 'Archived', tone: 'neutral' },
  // The outcome pills (OLIO-3172). Green/red rather than the terminal neutral of Canceled and
  // Archived: this is the answer the column exists to give, and the colors are the ones the
  // referral page already badges the decision with, per the ticket's "must match the detail
  // view". Also not in LEGEND — an outcome is not in-flight work either.
  //
  // `notSelected` deliberately shares the `danger` tone with `error`, so red in this column no
  // longer means only "this run is broken" — and since the outcome is not gated on the run being
  // finished, the two now appear side by side on the Active tab. Raised in review and kept on
  // purpose (product call, OLIO-3172): matching the referral page's red badge beat reserving red
  // for failures. The words are what tell them apart.
  selected: { label: 'Selected', tone: 'success' },
  notSelected: { label: 'Not Selected', tone: 'danger' },
  // Same treatment as Not Selected (product call, OLIO-3170): the referral page badges a denial
  // in the same red, and the column matches the page.
  denied: { label: 'Denied', tone: 'danger' },
};

// Most-urgent first — drives the overall pill (the most significant category wins). Error leads
// so a failed step is never masked by a ready-for-review one (matches categoryStatus's order).
const PRIORITY: ListStatus[] = ['error', 'actionNeeded', 'readyForReview', 'processing', 'pending', 'complete'];

// One segment per category, colored by that category's status.
export function categorySegments(workflow: Workflow): { key: string; name: string; status: ListStatus }[] {
  return workflow.actionsByCategory().map(({ category, actions }) => ({
    key: category.key,
    name: category.name,
    status: STATUS_TO_LIST[categoryStatus(actions)],
  }));
}

// Only the three outcomes this column has a word for. An unrecognized value (a client shipping its
// own vocabulary, or a slug from a future outcome) deliberately maps to nothing, so the pill falls
// back to the lifecycle status rather than printing a raw slug at a coordinator — the same
// defensive read the referral page makes ("Recorded outcome: <value>" rather than facility rows).
const OUTCOME_TO_LIST: Record<string, ListStatus> = {
  [OUTCOME_SELECTED]: 'selected',
  [OUTCOME_NOT_SELECTED]: 'notSelected',
  [OUTCOME_DENIED]: 'denied',
};

// How the referral ENDED, as a list status — null until an outcome has been recorded (OLIO-3172).
// Reads the VALUE, and normalizes, for the same reason every other reader does: the decision
// writes the value, and referrals decided before OLIO-2736 still hold won/lost.
//
// The value, not `Workflow#outcome`, even though the API denormalizes the decision onto that
// column too. The OLIO-2736 rename migration rewrote `workflow_values.value` and the action's
// outputs but did NOT backfill `workflows.outcome`, so a referral decided before it holds
// 'selected' here and a stale 'won' there. This side is the migrated one.
export function recordedOutcomeStatus(workflow: Workflow): ListStatus | null {
  return OUTCOME_TO_LIST[recordedOutcome(workflow)] ?? null;
}

// Has every facility THIS VIEWER holds denied the referral? (OLIO-3170, the Active-tab reading.)
//
// Viewer-relative by construction rather than by profile lookup: the API only serializes the
// facility-decision rows and verdicts for buildings the viewer holds (workflows index/show,
// `visible_to_group_ids`), so the FacilityDecision actions on this row ARE the viewer's buildings.
// A client-wide viewer sees every building, and for them this agrees with the cascade — once every
// building has denied, the API records the Denied outcome and `recordedOutcomeStatus` answers
// first. A building that has not decided yet has a row but no verdict, so it fails the check.
//
// Deliberately not a category rollup: the progress-bar segments keep speaking in lifecycle states,
// and a denied building's row is simply `completed` to them.
export function deniedByEveryVisibleBuilding(workflow: Workflow): boolean {
  const decisions = workflow.actions.filter((action) => action.kind === 'FacilityDecision');
  return decisions.length > 0 && decisions.every((action) => action.isCompleted && facilityDenied(workflow, action));
}

// This building's OWN verdict is denied. `valueFor` falls back to the referral-level row when a
// building has none, which is right for a value a building can inherit and wrong for a verdict —
// the same rule the API's FacilityDecision.determination_for applies — so the row must carry the
// building's group id to count.
function facilityDenied(workflow: Workflow, action: WorkflowAction): boolean {
  if (!action.groupId) return false;
  const verdict = workflow.valueFor('facility_determination', { groupId: action.groupId });
  return verdict?.groupId === action.groupId && verdict.value === 'denied';
}

// The two categories a denial concludes (OLIO-3170): the per-building decisions that produced it,
// and the Referral Outcome it was recorded as. Both read red "Denied" — the card badge and the rail
// icon — once the API has recorded the denied outcome; every other category keeps its rollup (the
// patient identification and auto-check work really did complete).
//
// The Facility Decision(s) category ALSO reads Denied on a live referral when every facility this
// viewer can see has denied — the same viewer-relative reading the list pill gives, so a building
// that has said no does not open the referral to a green "Complete" while other buildings are still
// deciding. The Referral Outcome does not: nothing has been recorded there yet, and it keeps saying
// so. (The recorded-outcome half is run-level and visible to all, so on a denied referral every
// viewer sees the same two badges.)
export function concludedByDenial(workflow: Workflow, actions: WorkflowAction[]): boolean {
  const denied = isDeniedOutcome(workflow);
  if (actions.some((action) => action.kind === 'AcceptReferral')) return denied;
  if (actions.some((action) => action.kind === 'FacilityDecision')) {
    return denied || deniedByEveryVisibleBuilding(workflow);
  }
  return false;
}

// The list pill: the recorded outcome once there is one, else the highest-priority category status.
// A canceled run (e.g. confirmed duplicate, OLIO-2729) says so — the all-canceled category rollup
// would otherwise read as Pending, indistinguishable from not-started.
//
// A decided referral used to read "Complete" whatever was chosen (OLIO-3172). That is true of the
// RUN and useless to a coordinator: the column is called Current Status, and the current status of
// a decided referral is Selected or Not Selected. "Complete" survives as the fallback for a run
// that finished without a recorded outcome.
//
// Deliberately NOT gated on the run being finished, because recording the decision does not finish
// it: `Workflow::Evaluator#record_decision` denormalizes the outcome and sweeps pre-decision work
// but leaves the run OPEN, and at a client with a patient-writable EHR the SyncPatientToEhr step
// then activates to action_required awaiting a person. Gating on completion would have shown the
// outcome for Not Selected (where the sync parks `skipped` and the run finishes by exhaustion at
// decision time) while silently never showing it for Selected at exactly those clients. Product
// call, Meg on OLIO-3172: the outcome shows as soon as it is recorded, on both tabs.
//
// The one thing that still outranks it is an errored step — the standing rule in this file, the
// same reason categoryStatus ranks ERRORED first. A referral whose EHR push is merely OUTSTANDING
// reads its outcome, and the Progress column next door still names the step it is on.
//
// On a LIVE run with no recorded outcome, the viewer-relative Denied (OLIO-3170) comes next: a
// referral still open for other buildings, but over for every building this viewer holds. Ranked
// below a recorded outcome because a person's Selected / Not Selected is the referral's answer
// whatever this viewer's buildings said; below a finished run because a completed referral's word
// is its outcome (or Complete), not its buildings' verdicts; above the in-flight rollup because
// "Processing" for someone whose every building has already said no is not their current status.
// An errored step outranks it as it outranks everything else.
export function overallListStatus(workflow: Workflow): ListStatus {
  if (workflow.status === WorkflowStatus.CANCELED) return workflow.isArchived ? 'archived' : 'canceled';

  const present = new Set(categorySegments(workflow).map((segment) => segment.status));
  const outcome = recordedOutcomeStatus(workflow);
  if (outcome && !present.has('error')) return outcome;

  // Unchanged below: a run with no readable outcome reports its lifecycle exactly as before,
  // except for the viewer-relative Denied on a live run.
  if (workflow.isCompleted) return 'complete';
  if (!present.has('error') && deniedByEveryVisibleBuilding(workflow)) return 'denied';
  return PRIORITY.find((status) => present.has(status)) ?? 'processing';
}

// "X of N – <current category>" — current is the first not-yet-complete category (the last
// once everything's done).
export function progressSummary(workflow: Workflow): { index: number; total: number; categoryName: string } {
  const categories = workflow.actionsByCategory();
  const total = categories.length;
  const currentIndex = categories.findIndex(({ actions }) => categoryStatus(actions) !== ActionStatus.COMPLETED);
  const resolvedIndex = currentIndex === -1 ? total - 1 : currentIndex;
  return {
    index: resolvedIndex + 1,
    total,
    categoryName: categories[resolvedIndex]?.category.name ?? '',
  };
}

// An open discovery sweep is a standing "go look at this portal" task for a person, so a
// list of runs pins it to the top. "Open" means the sweep's
// DiscoverPortalActivity action still sits at action_required — deliberately NOT
// Workflow#isTerminal, which would keep the pin through the run's automated remainder
// after a person has already handed the work off.
export function isOpenSweep(workflow: Workflow): boolean {
  if (workflow.definition.apiName !== PORTAL_DISCOVERY_API_NAME) return false;
  // A terminal run needs nobody, whatever its action rows say. Cancellation normally sweeps
  // action_required actions to canceled, but older runs canceled before that behavior (or via
  // console) can keep a stale action_required row — don't let one pin and label a dead sweep.
  if (workflow.isTerminal) return false;
  return workflow.actions.some((action) => action instanceof DiscoverPortalActivityAction && action.isActionRequired);
}

// List order for the shared workflow inbox: open sweeps pin to the top; everything else —
// and a sweep once its work is done — sorts by createdAt descending. The explicit tiebreak
// is what keeps the order stable across refetches: the index is nominally recent-first but
// unordered by contract. Client-side ordering holds only while the index is unpaginated;
// when it goes server-side this becomes an ORDER BY there.
export function orderWorkflows(workflows: Workflow[]): Workflow[] {
  const rank = (workflow: Workflow) => (isOpenSweep(workflow) ? 0 : 1);
  return [...workflows].sort(
    (a, b) => rank(a) - rank(b) || (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)
  );
}
